import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/feature/Seo";
import TopicHero from "@/pages/topic/components/TopicHero";
import TopicArticle from "@/pages/topic/components/TopicArticle";
import TopicComments from "@/pages/topic/components/TopicComments";
import TopicAside from "@/pages/topic/components/TopicAside";
import { topicMap } from "@/mocks/topics";
import { topicDetails } from "@/mocks/topicDetails";
import { categoryMap } from "@/mocks/categories";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = id ? topicMap[id] : undefined;
  const detail = id ? topicDetails[id] : undefined;

  if (!topic || !detail) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Seo
          title="话题不存在 - 51吃瓜榜"
          description="该话题可能已经下榜或链接有误，返回首页查看当前正在榜上的热门焦点排行。"
          keywords="51吃瓜榜,热门吃瓜榜,话题不存在"
          path={`/topic/${id ?? ""}`}
        />
        <Navbar />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-24 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">这个话题已经下榜了</h1>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            它可能热度回落暂时离开了榜单，或链接不太对。回首页看看今天正在榜上的焦点事件排行吧。
          </p>
          <Link
            to="/"
            className="mt-2 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页榜单
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const cat = categoryMap[topic.category];
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: topic.title,
      description: topic.summary,
      articleSection: cat?.name,
      url: `https://51.cg/topic/${topic.id}`,
      keywords: topic.tags.join(","),
      author: { "@type": "Organization", name: "51吃瓜榜" },
      publisher: { "@type": "Organization", name: "51吃瓜榜" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://51.cg/" },
        { "@type": "ListItem", position: 2, name: cat?.name, item: "https://51.cg/" },
        { "@type": "ListItem", position: 3, name: topic.title, item: `https://51.cg/topic/${topic.id}` },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Seo
        title={`${topic.title} - 51吃瓜榜热点话题详情`}
        description={`${topic.summary} 查看该话题的事件回顾时间线与网友热评，了解为什么它能登上51吃瓜榜今日焦点排行。`}
        keywords={`${topic.title},${topic.tags.join(",")},51吃瓜榜,热门吃瓜榜,热门事件排行`}
        path={`/topic/${topic.id}`}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="flex-1">
        <TopicHero topic={topic} />

        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="flex flex-col gap-10 lg:col-span-8">
              <TopicArticle detail={detail} />
              <TopicComments comments={detail.comments} />
            </div>
            <aside className="lg:col-span-4">
              <TopicAside
                currentId={topic.id}
                related={detail.related}
                rank={topic.rank}
                heat={topic.heat}
                category={topic.category}
              />
            </aside>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}